import { useCallback, useContext, useMemo } from 'react';
import { showNotification } from '@mantine/notifications';

import { ContestManageContext } from '../ContestManageContext';
import { reportCreateContestNextSectionClick } from '../../analytics';

export const useSectionsNavigation = () => {
  const { currentSection, setCurrentSection, allSectionsKeys, errors, values } =
    useContext(ContestManageContext);

  const currentSectionIndex = useMemo(() => {
    const current = allSectionsKeys.findIndex(
      (item) => item.path.join(',') === currentSection?.path.join(',')
    );
    return current;
  }, [allSectionsKeys, currentSection]);

  const prevSectionKeyItem = useMemo(
    () => allSectionsKeys[currentSectionIndex - 1],
    [allSectionsKeys, currentSectionIndex]
  );

  const nextSectionKeyItem = useMemo(() => {
    const currentIndex = allSectionsKeys.findIndex(
      (item) => item.path.join(',') === currentSection?.path.join(',')
    );
    return allSectionsKeys[currentIndex + 1];
  }, [allSectionsKeys, currentSection]);

  const handleNextSectionClick = useCallback(
    (scrollTo = true) => {
      if (errors && errors[currentSection.key]) {
        showNotification({
          title: 'Missing Information',
          message:
            'It looks like you’ve forgotten to make a selection for one or more things on this step',
          color: 'orange.3',
          autoClose: 5000,
        });
        return;
      }
      if (!nextSectionKeyItem) {
        return;
      }

      reportCreateContestNextSectionClick({
        sectionKey: currentSection.key,
        values: values[currentSection.key] ?? {},
      });

      setCurrentSection(nextSectionKeyItem);
      if (scrollTo) {
        window.scrollTo({ top: 0 });
      }
    },
    [currentSection?.key, errors, nextSectionKeyItem, setCurrentSection, values]
  );

  const handlePrevSectionClick = useCallback(() => {
    // If section is skipped then go to the previous section
    // skipped section has SECTION_NOT_LOADED error
    if (prevSectionKeyItem.error === 'SECTION_NOT_LOADED') {
      setCurrentSection(allSectionsKeys[currentSectionIndex - 2]);
      return;
    }
    if (!prevSectionKeyItem) {
      return;
    }
    setCurrentSection(prevSectionKeyItem);
    window.scrollTo({ top: 0 });
  }, [allSectionsKeys, currentSectionIndex, prevSectionKeyItem, setCurrentSection]);

  const handleGoToSection = useCallback(
    (sectionKey, query?: string) => {
      const section = allSectionsKeys.find((item) => item.key === sectionKey);
      if (query) {
        // no-op
      }
      if (section) {
        setCurrentSection(section);
        window.scrollTo({ top: 0 });
      }
    },
    [allSectionsKeys, setCurrentSection]
  );

  return {
    currentSection,
    isNextSection: !!nextSectionKeyItem,
    nextSectionKeyItem,
    isPrevSection: !!prevSectionKeyItem,
    prevSectionKeyItem,
    onNextSectionClick: handleNextSectionClick,
    onPrevSectionClick: handlePrevSectionClick,
    onGoToSection: handleGoToSection,
  };
};
